import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCampaignActionsDialogs } from '../../../CampaignActionsDialogs/CampaignActionsDialogsProvider'

import { GivingCampaignStatusDto } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useToast } from '@percent/lemonade'

export type UseGivingCampaignActionsProps = Readonly<{
  campaignId: string
  campaignHref: string
  campaignEditHref: string
  campaignTitle: string
  campaignStatus: GivingCampaignStatusDto
}>

export type UseGivingCampaignActionsReturn = Readonly<{
  view: () => void
  edit: () => void
  publish: () => void
  unpublish: () => void
  delete: () => void
}>

export const useGivingCampaignActions = ({
  campaignId,
  campaignHref,
  campaignEditHref,
  campaignTitle,
  campaignStatus
}: UseGivingCampaignActionsProps): UseGivingCampaignActionsReturn => {
  const { userCan } = usePermissionCheck()
  const { addToast } = useToast()
  const { t } = useTranslation()

  const { publishCampaign, unpublishCampaign, deleteCampaign } = useCampaignActionsDialogs()

  const view = () => {
    window.open(campaignHref, '_blank', 'noopener noreferrer')
  }

  const edit = useCallback(() => {
    if (!userCan('manageGivingCampaigns')) {
      addToast(t('errorMessage.unauthorized'), 'error')

      return
    }

    if (campaignStatus === GivingCampaignStatusDto.CANCELLED) {
      addToast(t('errorMessage.manageGivingCampaigns.campaignDeleted'), 'error')

      return
    }

    window.open(campaignEditHref, '_blank', 'noopener noreferrer')
  }, [userCan, campaignStatus, campaignEditHref, t, addToast])

  const publish = useCallback(() => {
    if (!userCan('manageGivingCampaigns')) {
      addToast(t('errorMessage.unauthorized'), 'error')

      return
    }

    if (campaignStatus === GivingCampaignStatusDto.PUBLISHED) {
      return
    }

    if (campaignStatus === GivingCampaignStatusDto.CANCELLED) {
      addToast(t('errorMessage.manageGivingCampaigns.campaignDeleted'), 'error')

      return
    }

    publishCampaign({ id: campaignId, title: campaignTitle })
  }, [userCan, campaignStatus, campaignId, campaignTitle, t, addToast, publishCampaign])

  const unpublish = useCallback(() => {
    if (!userCan('manageGivingCampaigns')) {
      addToast(t('errorMessage.unauthorized'), 'error')

      return
    }

    if (campaignStatus === GivingCampaignStatusDto.DRAFT) {
      return
    }

    if (campaignStatus === GivingCampaignStatusDto.CANCELLED) {
      addToast(t('errorMessage.manageGivingCampaigns.campaignDeleted'), 'error')

      return
    }

    unpublishCampaign({ id: campaignId, title: campaignTitle })
  }, [userCan, campaignStatus, campaignId, campaignTitle, t, addToast, unpublishCampaign])

  const del = useCallback(() => {
    if (!userCan('manageGivingCampaigns')) {
      addToast(t('errorMessage.unauthorized'), 'error')

      return
    }

    if (campaignStatus === GivingCampaignStatusDto.CANCELLED) {
      return
    }

    deleteCampaign({ id: campaignId, title: campaignTitle })
  }, [userCan, campaignStatus, campaignId, campaignTitle, t, addToast, deleteCampaign])

  return { view, edit, publish, unpublish, delete: del }
}
